/**
 * Platform provider — builds an AreaContext exclusively from data the
 * platform actually holds: the location record from the API (name, region,
 * coordinates, nearby bases) and the curated locationVisuals photography.
 *
 * Rule: nothing here is fabricated. If a fact is unknown it is simply absent
 * from the context, and the panel omits that section entirely.
 */

import { getLocationVisuals, FALLBACK_VISUALS } from '../locationVisuals';

/**
 * @param {object} location  Location record from /api/locations (id, name,
 *                           region, lat, lng, nearby_bases?).
 * @param {string} locale    Active locale for visuals copy.
 * @returns AreaContext
 */
export function buildPlatformAreaContext(location, locale = 'en-US') {
  if (!location || !location.name) return null;

  const visuals = getLocationVisuals(location.name, locale) || {};
  const photos = [visuals.heroImage, ...(visuals.galleryImages || [])]
    .filter(Boolean)
    .filter((src, i, arr) => arr.indexOf(src) === i);

  return {
    mode: 'platform',
    location: {
      id: location.id,
      name: location.name,
      region: location.region || null,
      lat: Number.isFinite(location.lat) ? location.lat : null,
      lng: Number.isFinite(location.lng) ? location.lng : null,
    },
    tagline: visuals.tagline || null,
    bases: Array.isArray(location.nearby_bases) ? location.nearby_bases.filter(Boolean) : [],
    highlights: Array.isArray(visuals.lifestyleHighlights) ? visuals.lifestyleHighlights : [],
    localFlavor: Array.isArray(visuals.localFlavor) ? visuals.localFlavor : [],
    photos: photos.length > 0 ? photos : [FALLBACK_VISUALS.heroImage].filter(Boolean),
    // Enrichment slots — null/empty in platform mode; the google provider
    // fills these when a key is configured. The panel hides empty sections.
    streetView: null,
    places: [],
    routes: [],
  };
}
